import { Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SbSupportTextBlockComponent } from '@app/+modals/sb-support/blocks/text/sb-support-text-block.component';
import { fieldChangeWithStart } from '@app/forms/form.helper';
import { HubSpotService } from '@app/startup/hub-spot.service';
import { TranslatePipe } from '@ngx-translate/core';
import { PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { SwitchComponent } from '@sb/ui/switch';
import { CookieService } from 'ngx-cookie-service';
import { of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { SupportService } from '../../../support/support.service';

@Component({
  selector: 'sb-chat-widget',
  templateUrl: './sb-chat-widget.component.html',
  imports: [SwitchComponent, SbSupportTextBlockComponent, TranslatePipe, ReactiveFormsModule],
})
export class SbChatWidgetComponent implements OnInit {
  #cookieService = inject(CookieService);
  #supportService = inject(SupportService);
  #hubSpotService = inject(HubSpotService);
  #pageFilterService = inject(PageFiltersService);
  #destroyRef = inject(DestroyRef);

  hasConsent = input<boolean>(false);
  chatToggled = output<boolean>();

  hubSpotChatStatusCtrl = new FormControl(this.#cookieService.get('hubspotChatVisible') === 'true');

  #triggerConsent = new Subject();

  ngOnInit() {
    void this.#triggerConsent
      .pipe(
        switchMap(() => this.#pageFilterService.saveFilters({ crm_consent: true })),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: () => {
          this.#hubSpotService.crmConsent = true;
        },
      });

    void fieldChangeWithStart(this.hubSpotChatStatusCtrl)
      .pipe(
        tap((active) => this.chatToggled.emit(active)),
        switchMap((active) => {
          if (!active) {
            return of(false);
          }
          return this.#supportService.getHubSpotIdentificationToken();
        }),
        catchError(() => {
          this.hubSpotChatStatusCtrl.setValue(false);
          this.#cookieService.set('hubspotChatVisible', 'false', undefined, '/', undefined, true, 'Strict');
          return of(false);
        }),
        tap((active) => {
          this.#cookieService.set(
            'hubspotChatVisible',
            active ? 'true' : 'false',
            undefined,
            '/',
            undefined,
            true,
            'Strict',
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: (identification) => {
          if (!identification) {
            this.#hubSpotService.removeChatWidget();
            return;
          }

          if (!this.#hubSpotService.crmConsent) {
            this.#triggerConsent.next(null);
          }
          this.#hubSpotService.setIdentification(identification.email, identification.token);
          this.#hubSpotService.initializeChatWidget(true);
        },
        error: () => {
          this.hubSpotChatStatusCtrl.setValue(false);
        },
      });
  }
}
